import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import api from "@/lib/api";
import PageHero from "@/components/PageHero";
import { Container, SectionHeading, GoldButton, OutlineButton } from "@/components/shared";
import ListingCard from "@/components/ListingCard";

const IMG = "https://images.pexels.com/photos/8755712/pexels-photo-8755712.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1200";

export default function Lincolnshire() {
  const [listings, setListings] = useState([]);
  useEffect(() => {
    api.get("/listings", { params: { shore: "lincolnshire" } }).then((r) => setListings(r.data));
  }, []);

  return (
    <div>
      <PageHero
        kicker="The mother town"
        title="Boston, Lincolnshire"
        intro="A Fenland market town on the River Witham in the east of England, gathered beneath the Stump — the great tower of St Botolph's, visible for miles across the flat marsh country. This is where the name began."
      >
        <div className="flex flex-wrap gap-3">
          <GoldButton as="link" to="/visit/lincolnshire">Plan a visit</GoldButton>
          <OutlineButton as="link" to="/massachusetts" className="px-6 py-3">See the daughter city →</OutlineButton>
        </div>
      </PageHero>

      <section className="py-16 sm:py-20">
        <Container>
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-6">
              <img src={IMG} alt="St Botolph's, the Stump" className="w-full h-[420px] object-cover border border-[#D6D3CB]" />
            </div>
            <div className="lg:col-span-6">
              <SectionHeading title="A town that steers by its tower" />
              <div className="space-y-4 font-sans text-base text-[#1B2430] leading-relaxed mt-6">
                <p>
                  Boston grew rich in the Middle Ages as a wool port, trading with the Hanseatic
                  towns of the North Sea. When the trade shifted, the Stump remained — a landmark for
                  sailors on the Wash and a gathering place for the town.
                </p>
                <p>
                  In the early seventeenth century it became a centre of strong Puritan feeling.
                  Its vicar from 1612, John Cotton, drew people who wanted a plainer church than the
                  crown allowed. Many of them would later cross the Atlantic and carry the town's
                  name with them.
                </p>
                <p>
                  Today it remains a working market town: the Wednesday and Saturday markets, the
                  Haven still busy with boats, and makers keeping old trades in good repair.
                </p>
              </div>
            </div>
          </div>
        </Container>
      </section>

      <section className="pb-16 sm:pb-24">
        <Container>
          <div className="flex items-end justify-between gap-4 mb-8">
            <SectionHeading kicker="Made here" title="Makers on the Lincolnshire shore" />
            <OutlineButton as="link" to="/made" className="whitespace-nowrap">All makers <ArrowRight size={15} className="ml-2" /></OutlineButton>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            {listings.map((l) => (
              <ListingCard key={l.id} listing={l} />
            ))}
          </div>
        </Container>
      </section>
    </div>
  );
}
